import { useLanguage } from '../hooks/useLanguage';
import { getDestinationData } from './destinationData';
import { getActivityData } from './activityData';

export interface LocalizedDestinationData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  image: string;
  location: string;
  region: string;
  elevation: string;
  bestTime: string;
  highlights: string[];
  activities: string[];
  gallery: string[];
  mapLocation: {
    lat: number;
    lng: number;
  };
  additionalDetails?: {
    altitude?: string;
    surface?: string;
    population?: string;
    density?: string;
    length?: string;
  };
}

export interface LocalizedActivityData {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  image: string;
  duration: string;
  difficulty: string;
  season: string;
  price: string;
  locations: string[];
  requirements: string[];
  included: string[];
  highlights: string[];
  gallery: string[];
  carouselEnabled?: boolean;
  creditsInfoRelativePath?: string;
  animals?: Array<{
    id: 'wolf' | 'dear';
    image: string;
    name: string;
    alt: string;
    description: string;
    travelNote?: string;
  }>;
}

export const useLocalizedDestinationData = (destinationId: string): LocalizedDestinationData | null => {
  const { t } = useLanguage();
  
  // Get the original data for images, gallery, and map location
  const originalData = getDestinationData(destinationId);
  
  if (!originalData) {
    return null;
  }

  // For now, return the original data with basic translations
  // We'll need to implement a different approach for arrays
  const translatedData = {
    ...originalData,
    name: t(`destinations.${destinationId}.name`) || originalData.name,
    subtitle: t(`destinations.${destinationId}.subtitle`) || originalData.subtitle,
    description: t(`destinations.${destinationId}.description`) || originalData.description,
    location: t(`destinations.${destinationId}.location`) || originalData.location,
    region: t(`destinations.${destinationId}.region`) || originalData.region,
    elevation: t(`destinations.${destinationId}.elevation`) || originalData.elevation,
    bestTime: t(`destinations.${destinationId}.bestTime`) || originalData.bestTime,
    highlights: originalData.highlights, // Keep original for now
    activities: originalData.activities  // Keep original for now
  };

  return translatedData;
};

export const useLocalizedActivityData = (activityId: string): LocalizedActivityData | null => {
  const { t } = useLanguage();
  
  // Get the original data for images and gallery
  const originalData = getActivityData(activityId);
  
  if (!originalData) {
    return null;
  }

  // For now, return the original data with basic translations
  // We'll need to implement a different approach for arrays
  const translatedData = {
    ...originalData,
    name: t(`activities.${activityId}.name`) || originalData.name,
    subtitle: t(`activities.${activityId}.subtitle`) || originalData.subtitle,
    description: t(`activities.${activityId}.description`) || originalData.description,
    duration: t(`activities.${activityId}.duration`) || originalData.duration,
    difficulty: t(`activities.${activityId}.difficulty`) || originalData.difficulty,
    season: t(`activities.${activityId}.season`) || originalData.season,
    price: t(`activities.${activityId}.price`) || originalData.price,
    locations: originalData.locations, // Keep original for now
    requirements: originalData.requirements, // Keep original for now
    included: originalData.included, // Keep original for now
    highlights: originalData.highlights, // Keep original for now
    carouselEnabled: originalData.carouselEnabled,
    creditsInfoRelativePath: originalData.creditsInfoRelativePath,
    animals: originalData.animals?.map((a) => {
      const travelNoteKey = `curiosities.wildlife.items.${a.id}.travelNote`;
      const travelNoteValue = t(travelNoteKey);
      // If translation function returns the key itself, it means translation not found
      let travelNote = travelNoteValue === travelNoteKey ? undefined : (travelNoteValue || undefined);
      
      // Exclude travelNote for 'dear' and 'wolf' when used in activities (they should only show in curiosities page)
      if (a.id === 'dear' || a.id === 'wolf') {
        travelNote = undefined;
      }
      
      return {
        id: a.id,
        image: a.image,
        // Pull localized content from curiosities.wildlife
        name: t(`curiosities.wildlife.items.${a.id}.name`) || a.id,
        alt: t(`curiosities.wildlife.items.${a.id}.alt`) || a.id,
        description: t(`curiosities.wildlife.items.${a.id}.description`) || '',
        travelNote
      };
    })
  };

  return translatedData;
};
